import type { User } from '@store/slices/authSlice'
import { getCookieAuthHeaders } from '@utils/csrfCookie'

export type SessionRestoreResult =
  | { status: 'validated'; user: User }
  | { status: 'refreshed'; user: User; accessToken: string }
  | { status: 'invalid' }
  | { status: 'unavailable' }

function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split('.')
    if (parts.length !== 3) return null
    const payload = parts[1]
    const padded = payload.replace(/-/g, '+').replace(/_/g, '/').padEnd(
      payload.length + (4 - (payload.length % 4)) % 4,
      '='
    )
    return JSON.parse(atob(padded))
  } catch {
    return null
  }
}

export function isAccessTokenExpired(token: string): boolean {
  const payload = decodeJwtPayload(token)
  if (!payload || typeof payload.exp !== 'number') return true
  return payload.exp < Date.now() / 1000 + 10
}

async function sleep(ms: number): Promise<void> {
  await new Promise((resolve) => setTimeout(resolve, ms))
}

async function fetchWithRetry(
  input: string,
  init: globalThis.RequestInit | undefined,
  fetchFn: typeof fetch,
  retries = 4,
  retryDelayMs = 1500
): Promise<Response> {
  let lastError: unknown
  for (let attempt = 0; attempt <= retries; attempt++) {
    try {
      return await fetchFn(input, init)
    } catch (err) {
      lastError = err
      if (attempt < retries) {
        await sleep(retryDelayMs * (attempt + 1))
      }
    }
  }
  throw lastError
}

function isAuthFailureStatus(status: number): boolean {
  return status === 401 || status === 403
}

async function fetchProfile(
  accessToken: string,
  fetchFn: typeof fetch,
  retries: number,
  retryDelayMs: number
): Promise<User | 'auth_failed' | 'unavailable'> {
  let res: Response
  try {
    res = await fetchWithRetry('/api/v1/auth/profile', {
      headers: { authorization: `Bearer ${accessToken}` },
    }, fetchFn, retries, retryDelayMs)
  } catch {
    return 'unavailable'
  }

  if (!res.ok) {
    return isAuthFailureStatus(res.status) ? 'auth_failed' : 'unavailable'
  }

  const data = await res.json()
  if (data.success && data.data) {
    return data.data as User
  }
  return 'auth_failed'
}

async function refreshAccessToken(
  fetchFn: typeof fetch,
  retries: number,
  retryDelayMs: number
): Promise<{ accessToken: string } | 'invalid' | 'unavailable'> {
  let res: Response
  try {
    res = await fetchWithRetry('/api/v1/auth/refresh', {
      method: 'POST',
      credentials: 'include',
      headers: getCookieAuthHeaders(),
    }, fetchFn, retries, retryDelayMs)
  } catch {
    return 'unavailable'
  }

  if (!res.ok) {
    return isAuthFailureStatus(res.status) ? 'invalid' : 'unavailable'
  }

  const data = await res.json()
  if (data.success && data.data?.accessToken) {
    return {
      accessToken: data.data.accessToken as string,
    }
  }
  return 'invalid'
}

/**
 * Restores an authenticated session on app startup using stored tokens.
 * Retries transient network failures (e.g. cold backend after machine reboot).
 * Only returns `invalid` when the server explicitly rejects the credentials.
 */
export async function restoreSession(
  token: string | null,
  fetchFn: typeof fetch = fetch,
  options?: { retries?: number; retryDelayMs?: number }
): Promise<SessionRestoreResult> {
  const retries = options?.retries ?? 4
  const retryDelayMs = options?.retryDelayMs ?? 1500
  if (token && !isAccessTokenExpired(token)) {
    const profile = await fetchProfile(token, fetchFn, retries, retryDelayMs)
    if (profile !== 'auth_failed' && profile !== 'unavailable') {
      return { status: 'validated', user: profile }
    }
    if (profile === 'unavailable') {
      return { status: 'unavailable' }
    }
  }

  const refreshed = await refreshAccessToken(fetchFn, retries, retryDelayMs)
  if (refreshed === 'invalid') {
    return { status: 'invalid' }
  }
  if (refreshed === 'unavailable') {
    return { status: 'unavailable' }
  }

  const profile = await fetchProfile(refreshed.accessToken, fetchFn, retries, retryDelayMs)
  if (profile !== 'auth_failed' && profile !== 'unavailable') {
    return {
      status: 'refreshed',
      user: profile,
      accessToken: refreshed.accessToken,
    }
  }
  if (profile === 'unavailable') {
    return { status: 'unavailable' }
  }

  return { status: 'invalid' }
}
